/** @format */

import {
	Chain,
	Customer,
	IScoreCardSkuResponse,
	KillSwitches,
	SKUResolveEvent
} from '@shared/data-access/interfaces';
import { Component, DestroyRef, Input, OnInit, ViewEncapsulation, inject } from '@angular/core';
import {
	CookieService,
	EventBusService,
	Events,
	Store,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { Observable, forkJoin } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AuthLinkModule } from '@shared/authentication/utils';
import { CommonModule } from '@angular/common';
import { LoyaltyBalance } from '@scorecard/data-access';
import { Store as NgRxStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ToolTipModule } from '@shared/components';
import { selectResolvedSku } from '@pdp/data-access/state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'scorecard-scorecard-points',
	templateUrl: './scorecard-points.component.html',
	styleUrl: './scorecard-points.component.scss',
	encapsulation: ViewEncapsulation.None,
	imports: [AuthLinkModule, CommonModule, HomefieldIconLibraryModule, ToolTipModule]
})
export class ScorecardPointsComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly analyticsService = inject(ProductAnalyticsService);
	private readonly chain = inject(Chain);
	private readonly cookieService = inject(CookieService);
	private readonly eventBusService = inject(EventBusService);
	protected readonly window = inject(WINDOW);
	private readonly store = inject(Store);
	private readonly ngrxStore = inject(NgRxStore);
	private readonly utilityService = inject(UtilityService);
	readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	public pointsValue: string;
	public pointsShown = false;
	public displayText = false;
	public registeredUser = false;
	public displayUpTo = true;
	public rewardText = '(300 points = $10 Reward)';
	public greeting = 'Hi!';
	public loyalty;
	public scoreCardIcon = 'money-scorecard';
	public readonly linkClickEventName = 'PDPSignInRequest';
	public registeredUserFName: string;
	public shortenPurchasePathUpdatesATCEnabled = false;

	partNumber: string;

	@Input() SCORECARD_DATA: IScoreCardSkuResponse[];

	constructor() {}

	ngOnInit(): void {
		// make sure SCORECARD_DATA isn't an empty array
		if (this.SCORECARD_DATA?.length > 0) {
			this.getKillswitchValues();
			this.displayText = true;

			// get user first name from cookie
			this.registeredUserFName = this.cookieService.get('DCSG-ATHLETE-NAME');

			const loyaltyBalanceSubscription$ = this.store
				.selectDistinctKey('LOYALTY_BALANCE')
				.pipe(
					filter((data) => data !== undefined),
					take(1)
				) as Observable<LoyaltyBalance>;

			const customerSubscription$ = this.store.selectDistinctKey('CUSTOMER').pipe(
				filter((data) => data !== undefined),
				take(1)
			) as Observable<Customer>;

			forkJoin([loyaltyBalanceSubscription$, customerSubscription$]).subscribe(
				([loyaltyBalance, customer]: [LoyaltyBalance, Customer]) => {
					this.loyalty = loyaltyBalance;
					this.registeredUser = true;
					if (
						typeof this.registeredUserFName === 'string' &&
						this.registeredUserFName.trim() !== ''
					) {
						this.greeting = `Hi ${this.registeredUserFName}!`;
					}
					if (customer?.isScoreCardGold) {
						this.scoreCardIcon = 'money-scorecard-gold';
					}
				}
			);

			// Listen for the SkuResolve Event
			this.eventBusService.on(
				Events.SkuResolve,
				(skuResolveAnalyticsData: SKUResolveEvent) => {
					this.displayUpTo = true;
					for (const item of this.SCORECARD_DATA) {
						if (item.sku.toString() === skuResolveAnalyticsData.sku.partNumber) {
							const newValue = Math.trunc(parseInt(item.points, 10));
							this.pointsValue = String(newValue);
							this.pointsShown = true;
							this.displayUpTo = false;
						}
					}
				}
			);
		}

		this.ngrxStore
			.select(selectResolvedSku)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				this.partNumber = data && data.partNumber ? data.partNumber : null;
				this.calculatePointsValue();
			});
	}

	calculatePointsValue(): void {
		// check if SKU is resolved
		if (this.partNumber) {
			const skuPoints = this.SCORECARD_DATA.find((item) => (item.sku = this.partNumber));

			if (skuPoints !== undefined) {
				this.pointsValue = String(parseInt(skuPoints.points, 10));
				this.pointsShown = true;
				return;
			}
		}

		this.pointsValue = 'more';
		this.pointsShown = false;
	}

	private getKillswitchValues(): void {
		this.store
			.select<KillSwitches>('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(take(1))
			.subscribe((killSwitch) => {
				this.shortenPurchasePathUpdatesATCEnabled = this.utilityService.checkKillSwitch(
					'shortenPurchasePathUpdatesATCEnabled',
					killSwitch,
					this.chain.chainIdentifierAbbr
				);
			});
	}

	/**
	 * @description
	 * Click handler for the link, will trigger an analytics Event (PDPSignInRequest)
	 */
	public onLinkClick(): void {
		// Trigger the analytics Event
		this.analyticsService.triggerAnalyticsEvent(`${this.linkClickEventName}`);
	}
}
